import type { Elevator, GameState } from '../types.js';

/**
 * A bank of cars (ADR-0034).
 *
 * Every rule about a car is written for one: it reads `state.elevator`. A
 * second car does not get rules of its own. It runs by the same ones, with
 * `state.elevator` standing for it while they run, so two cars can never drift
 * apart in what they are allowed to do. A level with one car never comes here.
 */

/** Every car, the first first. */
export function carsOf(state: GameState): Elevator[] {
  return state.extraCars.length === 0 ? [state.elevator] : [state.elevator, ...state.extraCars];
}

/** A car by its place in the bank, or null if the level has no such car. */
export function carAt(state: GameState, index: number): Elevator | null {
  if (index === 0) return state.elevator;
  if (!Number.isInteger(index) || index < 0) return null;
  return state.extraCars[index - 1] ?? null;
}

/** Its place in the bank: 0 for the first. */
export function indexOfCar(state: GameState, car: Elevator): number {
  return car === state.elevator ? 0 : state.extraCars.indexOf(car) + 1;
}

/** The car someone is riding in, if they are riding. */
export function carCarrying(state: GameState, passengerId: string): Elevator | null {
  return carsOf(state).find((car) => car.riders.includes(passengerId)) ?? null;
}

/** Run a one-car rule for `car`: it stands in as `state.elevator` until the rule returns. */
export function onCar<T>(state: GameState, car: Elevator, rule: () => T): T {
  if (car === state.elevator) return rule();
  const first = state.elevator;
  state.elevator = car;
  try {
    return rule();
  } finally {
    state.elevator = first;
  }
}
